import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Languages, X, Loader2, CheckCircle, Play, Pause, Download, Eye, RefreshCw, Link2, FileAudio } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, need } from '../../utils/nodeRequirements';

// Idiomas destino/origen (BCP-47). 'auto' sólo aplica al idioma origen.
const LANGUAGES = [
  { id: 'es', label: 'Español' },
  { id: 'es-MX', label: 'Español (México)' },
  { id: 'es-AR', label: 'Español (Argentina)' },
  { id: 'en', label: 'English' },
  { id: 'en-GB', label: 'English (UK)' },
  { id: 'pt', label: 'Portugués' },
  { id: 'pt-BR', label: 'Portugués (Brasil)' },
  { id: 'fr', label: 'Francés' },
  { id: 'fr-CA', label: 'Francés (Canadá)' },
  { id: 'de', label: 'Alemán' },
  { id: 'it', label: 'Italiano' },
  { id: 'ja', label: 'Japonés' },
  { id: 'zh', label: 'Chino' },
  { id: 'ko', label: 'Coreano' },
  { id: 'ar', label: 'Árabe' },
  { id: 'hi', label: 'Hindi' },
  { id: 'ru', label: 'Ruso' },
  { id: 'nl', label: 'Neerlandés' },
  { id: 'pl', label: 'Polaco' },
  { id: 'tr', label: 'Turco' },
  { id: 'sv', label: 'Sueco' },
  { id: 'da', label: 'Danés' },
  { id: 'fi', label: 'Finlandés' },
  { id: 'el', label: 'Griego' },
  { id: 'cs', label: 'Checo' },
  { id: 'ro', label: 'Rumano' },
  { id: 'uk', label: 'Ucraniano' },
  { id: 'id', label: 'Indonesio' },
  { id: 'ms', label: 'Malayo' },
  { id: 'ta', label: 'Tamil' },
  { id: 'fil', label: 'Filipino' },
];

const MEDIA_ACCEPT = 'audio/mpeg,audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg,audio/flac,video/mp4,video/mov,video/webm';

const ElTraductorNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState(data.targetLanguage || 'es');
  const [sourceLanguage, setSourceLanguage] = useState(data.sourceLanguage || 'auto');
  const [cloningStrength, setCloningStrength] = useState(data.cloningStrength ?? 7);
  const [sourceUrl, setSourceUrl] = useState(data.sourceUrl || '');
  const [mediaLocal, setMediaLocal] = useState(data.mediaLocal || null);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openAudioPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  const connData = useCallback((handleId) => {
    const edge = edges.find(e => e.target === id && e.targetHandle === handleId);
    if (!edge) return null;
    const src = nodes.find(n => n.id === edge.source);
    return src?.data?.hasOutput ? src.data : null;
  }, [edges, nodes, id]);

  const audioConn = connData('inputAudio');
  const videoConn = connData('inputVideo');
  const media = audioConn || videoConn || mediaLocal;
  const hasMedia = !!media;
  const hasUrl = sourceUrl.trim().length > 0;

  useEffect(() => {
    updateNodeData(id, { targetLanguage, sourceLanguage, cloningStrength, sourceUrl, mediaLocal });
  }, [targetLanguage, sourceLanguage, cloningStrength, sourceUrl, mediaLocal]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`traductor-audio-${id}`);
    if (!audio) return;
    if (isPlaying) audio.pause(); else audio.play();
    setIsPlaying(!isPlaying);
  }, [id, isPlaying]);

  const pickMedia = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        setMediaLocal({ file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name });
      } else if (result.url) {
        setMediaLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['audio', 'video'], MEDIA_ACCEPT);
  }, [id, openSourcePicker]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!targetLanguage || (!hasMedia && !hasUrl)) return false;
    resetExec();

    const files = [];
    const params = {
      target_language: targetLanguage,
      source_language: sourceLanguage,
      cloning_strength: Number(cloningStrength),
    };

    if (hasMedia) {
      // El archivo/conexión tiene prioridad sobre la URL manual.
      let file = media.file;
      if (!file) {
        const url = media.outputUrl || media.preview;
        if (url?.startsWith('http')) {
          params.media_url = url;
        } else if (url) {
          file = await blobToFile(url, media.name || 'media.mp3', 'audio/mpeg');
        }
      }
      if (file) files.push({ key: 'media_url', file });
    } else {
      params.source_url = sourceUrl.trim();
    }

    const result = await processNode({
      resultType: 'dubbing',
      files,
      params,
    });

    if (result?.outputUrl) {
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'audio',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'audio',
        tool: 'elTraductor',
        filename: `dubbed_${targetLanguage}_${Date.now()}.flac`,
        url: result.outputUrl,
        prompt: `Doblaje a ${targetLanguage}`,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, targetLanguage, sourceLanguage, cloningStrength, sourceUrl, hasUrl,
      media, hasMedia, updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = !!targetLanguage && (hasMedia || hasUrl);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([need(!!targetLanguage, 'elegir el idioma de destino'), need(hasMedia || hasUrl, 'un video o audio (conectado o cargado) o un enlace')]);

  return (
    <div className={`node-container traductor-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="traductor-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Input handles: audio / video a doblar */}
      <Handle type="target" position={Position.Left} id="inputAudio" className="node-handle input-handle audio-handle" style={{ top: '25%' }} data-testid="traductor-input-audio" />
      <Handle type="target" position={Position.Left} id="inputVideo" className="node-handle input-handle video-input-handle" style={{ top: '45%' }} data-testid="traductor-input-video" />

      {/* Header */}
      <div className="node-header traductor-header">
        <div className="node-header-left">
          <Languages size={16} />
          <span>El_Traductor</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-traductor">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content traductor-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivo...' :
                 execStatus === 'queued' ? 'En cola...' :
                 `Doblando a ${targetLanguage}...`}
              </span>
            </div>
            <p className="node-processing-hint">Conserva voz, emoción y timing del hablante</p>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result qwen3tts-result">
            <audio
              id={`traductor-audio-${id}`}
              src={data.outputUrl}
              onEnded={() => setIsPlaying(false)}
            />
            <div className="qwen3tts-player">
              <button className="qwen3tts-play-btn" onClick={togglePlay} data-testid="traductor-play-btn">
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <div className="qwen3tts-waveform">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={`wave-${i}`}
                    className={`qwen3tts-wave-bar ${isPlaying ? 'animate' : ''}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openAudioPreview(data.outputUrl, 'El_Traductor - Doblaje')} data-testid="traductor-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `dubbed_${targetLanguage}_${Date.now()}.flac`); }} data-testid="traductor-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="audio" label="elTraductor" testIdPrefix="traductor-timeline" />
            </div>
            <div className="nano-regen-section">
              <button className="nano-regen-btn traductor-regen-btn" onClick={handleRegenerate} data-testid="traductor-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="traductor-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Doblaje a {targetLanguage} listo</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="traductor-reconfigure-notice"
              />
            )}

            {/* Fuente: archivo/conexión o URL */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <FileAudio size={12} />
                Audio o video a doblar *
              </span>
              <div className="nano-images-row">
                {media ? (
                  <div className="nano-img-thumb traductor-media-thumb" title={media.name || 'media'}>
                    <FileAudio size={16} />
                    {!audioConn && !videoConn && (
                      <button className="nano-clear-btn" onClick={() => setMediaLocal(null)} data-testid="traductor-clear-media">
                        <X size={8} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button className="nano-add-img-btn" onClick={pickMedia} title="Subir o elegir de la galería" data-testid="traductor-add-media">
                    <Play size={13} />
                  </button>
                )}
              </div>
              {audioConn && <span className="traductor-conn-note">Audio conectado</span>}
              {videoConn && <span className="traductor-conn-note">Video conectado</span>}
              {!hasMedia && (
                <div className="traductor-url-row">
                  <Link2 size={11} />
                  <input
                    type="text"
                    className="nano-select traductor-url-input"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    placeholder="o pegá una URL pública (directa, YouTube, TikTok)"
                    data-testid="traductor-source-url"
                  />
                </div>
              )}
            </div>

            {/* Idiomas */}
            <div className="nano-options-row traductor-options">
              <div className="nano-option">
                <span className="nano-option-label">Idioma destino *</span>
                <select value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)} className="nano-select" data-testid="traductor-target-language">
                  {LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Idioma origen</span>
                <select value={sourceLanguage} onChange={(e) => setSourceLanguage(e.target.value)} className="nano-select" data-testid="traductor-source-language">
                  <option value="auto">Auto-detectar</option>
                  {LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Cloning strength */}
            <div className="eleV2-slider-row traductor-strength-row">
              <span className="nano-option-label">Clonado de voz</span>
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={cloningStrength}
                onChange={(e) => setCloningStrength(parseInt(e.target.value, 10))}
                className="videoSound-slider"
                data-testid="traductor-cloning-strength"
              />
              <span className="videoSound-duration-value">{cloningStrength}</span>
            </div>
            <span className="traductor-hint">Mayor = más fiel a la voz original · menor = entrega más natural en el idioma destino</span>

            <button
              className="node-process-btn traductor-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="traductor-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : `Doblar a ${targetLanguage}`}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle audio-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

ElTraductorNode.displayName = 'ElTraductorNode';

export default ElTraductorNode;
